/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples — copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { ApiError } from "@/lib/api/client";
import {
  adminActivityActions,
  adminActivityAnalytics,
  adminDeleteUser,
  adminGetUser,
  adminListActivity,
  adminListUsers,
  adminUpdateUser,
  getActivity,
  getAuthConfig,
  getHealth,
  getMe,
  getMeDetail,
  getMyVote,
  getVoteSummary,
  getVoteThemes,
  loginWithPassword,
  type LoginWithPasswordResponse,
  logout,
  resetVotes,
  submitVote,
  updateProfile,
  type VoteSummaryResponse,
  type VoteThemesResponse,
} from "@/lib/api/generated";

// ─── Public queries / mutations ──────────────────────────────────────────

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const { data } = await getHealth();
      return data;
    },
  });
}

export function useAuthConfig() {
  return useQuery({
    queryKey: ["auth", "config"],
    queryFn: async () => {
      const { data } = await getAuthConfig();
      return data;
    },
    // The provider is fixed at deploy time — never refetch it.
    staleTime: Infinity,
  });
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await getMe();
      return data;
    },
    // Don't retry on a 401 — ProtectedRoute will redirect to /login.
    retry: false,
  });
}

export function useLoginWithPassword() {
  const queryClient = useQueryClient();
  return useMutation<
    LoginWithPasswordResponse | undefined,
    ApiError,
    { email: string; password: string }
  >({
    mutationFn: async (vars) => {
      const { data } = await loginWithPassword({ body: vars });
      return data;
    },
    onSuccess: (me) => {
      // Seed "me" from the response before invalidating: the pre-login query
      // is in its 401 error state, and ProtectedRoute would bounce back to
      // /login if it read that while the refetch was still in flight.
      queryClient.setQueryData(["me"], me);
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear the entire cache so no stale per-user data leaks across sessions.
      queryClient.clear();
    },
  });
}

// ─── Profile ─────────────────────────────────────────────────────────────

export function useMeDetail() {
  return useQuery({
    queryKey: ["me", "detail"],
    queryFn: async () => {
      const { data } = await getMeDetail();
      return data;
    },
    retry: false,
  });
}

export function useActivity() {
  return useQuery({
    queryKey: ["activity"],
    queryFn: async () => {
      const { data } = await getActivity();
      return data ?? [];
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { firstName: string; lastName: string }) => {
      await updateProfile({
        body: { first_name: vars.firstName, last_name: vars.lastName },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

// ─── Admin ───────────────────────────────────────────────────────────────

export function useAdminUsers() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => {
      const { data } = await adminListUsers();
      return data ?? [];
    },
  });
}

export function useAdminUser(userId: string | null) {
  return useQuery({
    queryKey: ["admin", "users", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      const { data } = await adminGetUser({ path: { user_id: userId } });
      return data;
    },
    enabled: !!userId,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      userId: string;
      firstName?: string;
      lastName?: string;
      role?: string;
    }) => {
      await adminUpdateUser({
        path: { user_id: vars.userId },
        body: {
          first_name: vars.firstName,
          last_name: vars.lastName,
          role: vars.role,
        },
      });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users", vars.userId] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      await adminDeleteUser({ path: { user_id: vars.userId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

// ─── Admin activity ──────────────────────────────────────────────────────

export interface AdminActivityParams {
  search?: string;
  action?: string;
  userId?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export function useAdminActivity(params: AdminActivityParams = {}) {
  return useQuery({
    queryKey: ["admin", "activity", params],
    queryFn: async () => {
      const { data } = await adminListActivity({
        query: {
          search: params.search,
          action: params.action,
          user_id: params.userId,
          sort_by: params.sortBy ?? "created_at",
          sort_dir: params.sortDir ?? "desc",
          page: params.page ?? 1,
          page_size: params.pageSize ?? 25,
        },
      });
      return data;
    },
    // Keep the previous page on screen while fetching the next so the table
    // doesn't flash empty between paginations / filter changes.
    placeholderData: (prev) => prev,
  });
}

export function useAdminActivityAnalytics(days = 30) {
  return useQuery({
    queryKey: ["admin", "activity", "analytics", days],
    queryFn: async () => {
      const { data } = await adminActivityAnalytics({ query: { days } });
      return data;
    },
  });
}

export function useAdminActivityActions() {
  return useQuery({
    queryKey: ["admin", "activity", "actions"],
    queryFn: async () => {
      const { data } = await adminActivityActions();
      return data ?? [];
    },
  });
}

// ─── Demo voting ─────────────────────────────────────────────────────────

/**
 * The attendee's own vote, keyed by the httponly `boi-vote-id` cookie the
 * backend sets on first submit. This is how the public page knows whether to
 * show a fresh form or pre-fill an editable one — the cookie itself is not
 * readable from JS. `retry: false` so a fresh visitor (no cookie / 404-ish
 * "not voted" shape) settles immediately.
 */
export function useMyVote() {
  return useQuery({
    queryKey: ["votes", "me"],
    queryFn: async () => {
      const { data } = await getMyVote();
      return data;
    },
    retry: false,
  });
}

/**
 * Submit or edit a vote. Re-submitting under the same cookie edits in place,
 * so this invalidates both the attendee's own vote and the presenter summary.
 */
export function useSubmitVote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { score: number; pros: string[]; cons: string[] }) => {
      const { data } = await submitVote({
        body: { score: vars.score, pros: vars.pros, cons: vars.cons },
      });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["votes", "me"] });
      void queryClient.invalidateQueries({ queryKey: ["votes", "summary"] });
    },
  });
}

/**
 * Presenter-side score distribution. Cheap and poll-safe — the dialog passes
 * `refetchInterval` (~10s) while it is open so the live tally keeps ticking.
 */
export function useVoteSummary(opts: { enabled?: boolean; refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: ["votes", "summary"],
    queryFn: async () => {
      const { data } = await getVoteSummary();
      return data;
    },
    enabled: opts.enabled ?? true,
    refetchInterval: opts.refetchInterval,
  });
}

/**
 * LLM-clustered pros/cons for the revealed results panel. The backend keeps
 * this poll cheap (it skips the model call when no new votes have landed) and
 * returns clusters pre-sorted biggest-first, so the dialog polls it like the
 * summary rather than fetching once. `enabled` gates it to the revealed state;
 * `refetchInterval` (~10s) keeps it live — no manual refresh needed.
 */
export function useVoteThemes(opts: { enabled?: boolean; refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: ["votes", "themes"],
    queryFn: async () => {
      const { data } = await getVoteThemes();
      return data;
    },
    enabled: opts.enabled ?? false,
    refetchInterval: opts.refetchInterval,
    refetchOnWindowFocus: false,
  });
}

/**
 * Clear every vote and the server-side theme cache — lets a presenter reset
 * between a rehearsal and the live demo. Invalidates the summary and themes so
 * both refetch their now-empty state.
 */
export function useResetVotes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await resetVotes();
      return data;
    },
    onSuccess: () => {
      // Themes only mounts on the results view, so invalidating alone won't
      // refetch it while it's disabled — the panel would keep the old clusters
      // until a manual Refresh. Paint the wiped state directly so the histogram,
      // KPIs, and theme columns reflect the reset instantly (no stale flash
      // before the next 10s summary poll), then invalidate as a safety net for
      // whenever the queries are enabled.
      queryClient.setQueryData<VoteThemesResponse>(["votes", "themes"], {
        pros_clusters: [],
        cons_clusters: [],
        based_on_total: 0,
      });
      queryClient.setQueryData<VoteSummaryResponse>(["votes", "summary"], {
        total: 0,
        average: null,
        distribution: [1, 2, 3, 4, 5].map((score) => ({ score, count: 0 })),
      });
      void queryClient.invalidateQueries({ queryKey: ["votes", "summary"] });
      void queryClient.invalidateQueries({ queryKey: ["votes", "themes"] });
    },
  });
}
